import React, { type JSX } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import App from './App';
import './index.css';

// arbi-docs shares Sentry + PostHog projects with arbi / arbi-dev (one project
// per observability tool). Events are distinguished by the `environment` tag
// (Sentry) and super-property (PostHog) — filter for 'arbi-docs' (or
// 'arbi-docs-local') in either dashboard. Mode-conditional, same shape as
// arbi-dev, so local-dev runs stay separable from prod.
const SENTRY_ENV = import.meta.env.PROD ? 'arbi-docs' : 'arbi-docs-local';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: SENTRY_ENV,
  release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  enabled: import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN),
});

if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: 'history_change',
    person_profiles: 'identified_only',
    autocapture: true,
    loaded: (ph) => {
      ph.register({ environment: SENTRY_ENV });
      if (import.meta.env.MODE === 'development') ph.debug();
    },
  });
}

// main.tsx is the entry point — it has no exports by design; the local
// FallbackUI is only ever referenced from the createRoot call below.
// eslint-disable-next-line react-refresh/only-export-components
function FallbackUI(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-8">
      <div className="max-w-md text-center space-y-3">
        <div className="text-ink-bright text-lg font-semibold">Something went wrong</div>
        <div className="text-ink-secondary text-sm">
          The error has been logged. Try refreshing the page — if the problem persists, contact an
          admin.
        </div>
      </div>
    </div>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <Sentry.ErrorBoundary fallback={<FallbackUI />}>
        <App />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);
